import type { ApiClient, BookingFinance, FinanceTask } from '@elite-residence/api-client';
import { type Failure, failureOf } from '../ui/ui';

type Result<T> = { ok: true; body: T } | { ok: false; failure: Failure };
type History = Pick<BookingFinance, 'bookingId' | 'balances' | 'transactions' | 'nextCursor'>;

async function collect<T extends History>(read: (cursor?: string) => Promise<Result<T>>): Promise<Result<T>> {
  let first: T | undefined;
  const transactions: T['transactions'][number][] = [];
  const ids = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const result = await read(cursor);
    if (!result.ok) return result;
    const body = result.body;
    first ??= body;
    // A concurrent posting must not combine an earlier balance with a later
    // history. Ask the operator to refresh the whole view in that case.
    if (first.bookingId !== body.bookingId || JSON.stringify(first.balances) !== JSON.stringify(body.balances))
      return { ok: false, failure: { status: 409 } };
    for (const transaction of body.transactions) {
      if (ids.has(transaction.id)) return { ok: false, failure: { status: 409 } };
      ids.add(transaction.id);
      transactions.push(transaction);
    }
    cursor = body.nextCursor ?? undefined;
    if (cursor) {
      if (cursors.has(cursor)) return { ok: false, failure: { status: 409 } };
      cursors.add(cursor);
    }
  } while (cursor);
  return { ok: true, body: { ...first, transactions, nextCursor: null } };
}

export function loadBookingFinance(
  client: ApiClient,
  bookingId: string,
  signal: AbortSignal,
): Promise<Result<BookingFinance>> {
  return collect(async (cursor) => {
    const response = await client.getBookingFinance(
      {
        path: { bookingId },
        query: { limit: '500', ...(cursor ? { cursor } : {}) },
      },
      { signal },
    );
    return response.status === 200
      ? { ok: true, body: response.body }
      : { ok: false, failure: failureOf(response) ?? { status: response.status } };
  });
}

export function loadFinanceTask(
  client: ApiClient,
  transactionId: string,
  signal: AbortSignal,
): Promise<Result<FinanceTask>> {
  return collect(async (cursor) => {
    const response = await client.getFinanceTask(
      {
        path: { transactionId },
        query: { limit: '500', ...(cursor ? { cursor } : {}) },
      },
      { signal },
    );
    return response.status === 200
      ? { ok: true, body: response.body }
      : { ok: false, failure: failureOf(response) ?? { status: response.status } };
  });
}
